// Base Imports
import { useEffect } from 'react'

// import helpers and components
import getCollections from '@framework/api/content/getCollections'
import { LayoutWithTopStrip } from '@components/common/Layout'
import { NextHeadSEO } from '@components/common'

import { extractNumberFromHTMLpTag } from '@framework/utils/app-util'
import { THANK_YOU_DEFAULT_SLUG } from '@components/utils/constants'
import Script from 'next/script'
import { IS_SWOP_STORE_ENABLED } from '@framework/utils/constants'
import commerce from '@lib/api/commerce'
import withDataLayer, { PAGE_TYPES } from '@components/withDataLayer'
import { IDeviceInfo, useUI } from '@components/ui/context'
import { IProductResponseWithResultArray } from '@components/men/helpers/productResponse.model'
import { IImage } from '@components/utils/model/product.model'
import MenCollectionPage from '@components/men'
import { Redis } from '@framework/utils/redis-constants'
import {
  getDataByUID,
  parseDataValue,
  setData,
} from '@framework/utils/redis-util'
import {
  IProductsPayload,
  ProductService,
} from 'services/product/index.service'
import { IPagePropsProvider } from '@framework/contracts/page-props/IPagePropsProvider'
import { PagePropType, getPagePropType } from '@framework/page-props'
import useEventAnalytics from '@components/services/analytics/useEventAnalytics'
import { AnalyticsEventType } from '@components/services/analytics'

declare const window: any
declare const AF: any

interface IProps {
  deviceInfo: IDeviceInfo
  previousPath: string
  id: string
  products: IProductResponseWithResultArray
  allowFacets: boolean
  name: string
  completeSlug: string
  description: string
  customLinks: string
  customInfo1: string
  customInfo2: string
  customInfo3: string
  hostName: string
  metaTitle: string
  metaKeywords: string
  metaDescription: string
  images: IImage[]
  swopStorePageContents: any
  slug: any
}

function CollectionPage(props: IProps) {
  const getSrcForSwopStoreScript = () => {
    const str = swopStorePageContents?.script1?.toString()
    const srcRegex =
      /<script\s+src="([^"]+)"\s+type="text\/javascript"><\/script>/
    const match = str?.match(srcRegex)
    const srcValue = match ? match[1] : null
    return srcValue?.replaceAll(
      'SHOP_ID',
      extractNumberFromHTMLpTag(swopStorePageContents?.shopid)
    )
  }
  const {
    deviceInfo,
    previousPath,
    id,
    products,
    allowFacets,
    name,
    completeSlug,
    description,
    customLinks,
    customInfo1,
    hostName,
    metaTitle,
    metaKeywords,
    metaDescription,
    customInfo3,
    images,
    customInfo2,
    swopStorePageContents,
  } = props
  const { cartItems } = useUI()
  let data = { collection_id: props?.id , name: props?.name, slug: props?.slug, cartItems } 
  const { recordAnalytics } = useEventAnalytics()
  useEffect(()=>{
    recordAnalytics(AnalyticsEventType.PLP_VIEW, { type: 'collection', data })
  },[props?.slug])
  return (
    <div>
      {props?.hostName && (
        <input className="inst" type="hidden" value={props?.hostName} />
      )}

      <NextHeadSEO
        seoSettings={{
          ...props,
          title: props?.metaTitle,
          keywords: props?.metaKeywords,
          description: props?.metaDescription,
          images,
        }}
      />
      <MenCollectionPage
        deviceInfo={deviceInfo}
        previousPath={previousPath}
        id={id}
        products={products}
        allowFacets={!!allowFacets}
        name={name}
        completeSlug={completeSlug}
        description={description}
        customLinks={customLinks}
        customInfo1={customInfo1}
        customInfo2={customInfo2}
        customInfo3={customInfo3}
        hostName={hostName}
        metaTitle={metaTitle}
        metaKeywords={metaKeywords}
        metaDescription={metaDescription}
        images={images}
      />
      {props?.products?.results?.length > 0 && (
        <Script
          type="application/ld+json"
          id="schema"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "https://schema.org/",
                "@type": "ItemList",
                "itemListElement": ${JSON.stringify(
              props?.products?.results?.map(
                (product: any, pId: number) => ({
                  '@type': 'ListItem',
                  position: pId + 1,
                  name: product?.name,
                  url: 'https://www.damensch.com/' + product?.slug,
                })
              )
            )}
              }
            `,
          }}
        />
      )}
      {IS_SWOP_STORE_ENABLED &&
        swopStorePageContents?.shopid &&
        swopStorePageContents?.script1 && (
          <Script
            id="swopStore"
            strategy="lazyOnload"
            src={getSrcForSwopStoreScript()}
          />
        )}
    </div>
  )
}

CollectionPage.Layout = LayoutWithTopStrip

export async function getStaticProps({ params, ...context }: any) {
  const slug: any = params!.collection
  const machineId = 'getStaticProps-PLP'
  const cachedDataUID = {
    collectionUID: Redis.Key.Collection + '_' + slug,
    swopStorePageUID: Redis.Key.SwopStorePage + '_' + THANK_YOU_DEFAULT_SLUG,
  }
  const cachedData = await getDataByUID([
    cachedDataUID.collectionUID,
    cachedDataUID.swopStorePageUID,
  ])
  let collectionUIDData: any = parseDataValue(
    cachedData,
    cachedDataUID.collectionUID
  )
  let swopStorePageData: any = parseDataValue(
    cachedData,
    cachedDataUID.swopStorePageUID
  )
  if (!collectionUIDData) {
    const query: IProductsPayload = {
      slug: slug[0],
    }
    const res = await ProductService.retrieveProducts(query)
    collectionUIDData = res.data.products
    // collectionUIDData = await getCollectionBySlug(slug[0], machineId)
    await setData([
      { key: cachedDataUID.collectionUID, value: collectionUIDData },
    ])
  }

  if (!swopStorePageData) {
    const SwopStorePageContentsPromise = commerce.getPagePreviewContent({
      id: '', //pageId,
      slug: THANK_YOU_DEFAULT_SLUG, //slugs?.slug,
      workingVersion: process.env.NODE_ENV === 'production' ? true : true, // TRUE for preview, FALSE for prod.
      channel: '',
    })
    swopStorePageData = await SwopStorePageContentsPromise
    await setData([
      { key: cachedDataUID.swopStorePageUID, value: swopStorePageData },
    ])
  }
  const props: IPagePropsProvider = getPagePropType({ type: PagePropType.COMMON }) as IPagePropsProvider
  const commonPageProps = await props.getPageProps({ cookies: {} })
  return {
    props: {
      ...commonPageProps,
      ...(collectionUIDData?.plpPageData || {}),
      ...(collectionUIDData || {}),
      query: context,
      slug: params!.collection[0],
      completeSlug: params!.collection.join('/'),
      swopStorePageContents: swopStorePageData ?? {},
    },
    revalidate: 60,
  }
}

export async function getStaticPaths() {
  const data = await getCollections()
  return {
    paths: data
      ?.map((col: any) => {
        if (col.slug) {
          let collectionSlug =
            col.slug[0] === '/' ? `/men${col.slug}` : `/men/${col.slug}`
          return collectionSlug
        }
      })
      .filter((i: any) => !!i),
    fallback: true,
  }
}

export default withDataLayer(
  CollectionPage,
  PAGE_TYPES.Product,
  true,
  LayoutWithTopStrip
)
